import React, { useEffect, useState } from 'react';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Typography from '@mui/joy/Typography';
import api from '../../lib/api';
import type { EmailTemplate } from '../../types/marketing';

/** The contact fields the merge tags resolve against. Every one is optional:
    the 1:1 composers know a name and an address, rarely a full record. */
export interface MergeTagContact {
  first_name?: string;
  last_name?: string;
  full_name?: string;
  company_name?: string;
  email?: string;
}

const TAG_PATTERN = /\{\{\s*(\w+)\s*\}\}/g;

/**
 * Resolve `{{first_name}}` style tokens against the contact in scope.
 *
 * Mirrors MERGE_TAGS in backend/apexos/apps/marketing/email_utils.py so a
 * template written for a campaign reads the same in a 1:1 email. Unknown tags
 * are left intact, matching the backend, so a typo is visible in the draft
 * rather than silently blanked out on the way to a customer.
 */
export const renderMergeTags = (content: string, contact: MergeTagContact): string => {
  if (!content) return '';

  const fullName = contact.full_name
    || [contact.first_name, contact.last_name].filter(Boolean).join(' ');
  // The 1:1 composers know a display name, not a Contact record. Split it so
  // "Hi {{first_name}}," doesn't reach a customer as "Hi ,"; everything past
  // the first word is the surname, matching Contact.get_full_name's join.
  const nameParts = fullName.trim().split(/\s+/).filter(Boolean);
  const values: Record<string, string | undefined> = {
    first_name: contact.first_name || nameParts[0],
    last_name: contact.last_name || nameParts.slice(1).join(' '),
    full_name: fullName,
    company_name: contact.company_name,
    email: contact.email,
  };

  return content.replace(TAG_PATTERN, (match, key: string) =>
    key in values ? (values[key] ?? '') : match
  );
};

/** Best-effort plain text for a template authored as HTML. */
const toPlainText = (template: EmailTemplate): string =>
  template.plain_text_content
  || template.html_content
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .trim();

interface EmailTemplatePickerProps {
  contact: MergeTagContact;
  /** Receives the template's subject and body, merge tags already resolved. */
  onApply: (subject: string, body: string) => void;
  size?: 'sm' | 'md';
}

/**
 * "Use template" control over the organization's saved email templates.
 *
 * The templates already exist for Email Marketing; the 1:1 composers made
 * people retype the same routine message by hand. Renders nothing when the
 * org has no templates rather than offering an empty dropdown.
 */
export const EmailTemplatePicker: React.FC<EmailTemplatePickerProps> = ({
  contact,
  onApply,
  size = 'sm',
}) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    api.getEmailTemplates()
      .then(response => setTemplates(response.data.filter(t => !t.is_archived)))
      .catch(() => { /* the composer works fine without templates */ });
  }, []);

  if (templates.length === 0) return null;

  const handleChange = (templateId: number | null) => {
    setSelectedId(templateId);
    const template = templates.find(t => t.id === templateId);
    if (!template) return;
    onApply(
      renderMergeTags(template.subject, contact),
      renderMergeTags(toPlainText(template), contact),
    );
  };

  return (
    <Select
      size={size}
      value={selectedId}
      placeholder="Use a template"
      onChange={(_, value) => handleChange(value)}
      startDecorator={
        <Typography level="body-xs" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
          Template:
        </Typography>
      }
    >
      {templates.map(template => (
        <Option key={template.id} value={template.id}>
          {template.name}
        </Option>
      ))}
    </Select>
  );
};

export default EmailTemplatePicker;
